import React from 'react';
import { Box, DialogContent, DialogTitle } from '@mui/material';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { usePhotoRequest } from '../../hooks/photoRequest';

interface FormData {
  name: string;
  image: any;
}

interface AddNewPhotoProps {
  setDialogOpen: (open: boolean) => void;
}

const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  image: yup.mixed()
    .required("Image is required")
    .test("fileSize", "The file is too large", value => {
      const file = value as File;
      return file && file.size <= 22000000;
    })
    .test("fileType", "Unsupported File Format", value => {
      const file = value as File;
      return file && ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
    }),
});

const AddNewPhoto: React.FC<AddNewPhotoProps> = ({ setDialogOpen }) => {
  const photoRequest = usePhotoRequest();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<FormData>({
    resolver: yupResolver(validationSchema)
  });

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];    
    setValue("image", file as File);
  }

  const onSubmit = async (data: FormData) => {
    if (data.image) {
      const reader = new FileReader();
  
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const formattedData = {
          name: data.name,
          image: base64String
        };
        const dispatchData = await photoRequest({ method: 'POST', data: formattedData, id: null });
        if (dispatchData.meta.requestStatus === 'fulfilled') {
          setDialogOpen(false);
          await photoRequest({ method: 'GET', data: null, id: null });
        }
      };
  
      reader.readAsDataURL(data.image);
    }
  };
  

  return (
    <DialogContent>
      <DialogTitle sx={{ p: 0 }}>Add New Photo</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
        <TextField
          sx={{ display: 'block' }}
          fullWidth
          {...register('name')}
          label="Name"
          variant="standard"
          error={!!errors.name}
          helperText={errors.name ? errors.name.message : ''} 
          margin="normal"
        />
        <Box sx={{ mb: 5 }}></Box>
        <TextField
          type="file"
          variant='standard'
          inputProps={{ accept: "image/*" }}
          onChange={handleUploadFile}
          error={!!errors.image}
          helperText={errors.image ? (errors.image.message as string) : ''}
          sx={{ width: 300, display: 'block' }}
          margin="normal"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
          <Button sx={{ mr: 2 }} onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </Box>
      </form>
    </DialogContent>
  );
}

export default AddNewPhoto;
